import * as React from 'react';
import { MutationFunc } from 'react-apollo';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';

import { registrationPersonalDataFields, registrationBillingFields } from '../../../../formFields';
import { Button, Modal, Title, Input, Select } from '../../../../modules/modules';
import { CheckBox } from '../../../../components/FormGenerator/modules';
import CountryService from '../../../../services/CountryService';
import FlashStore from '../../../../services/FlashStore';
import { getCountyStateRequired } from '../../../../helpers/country/getCountyStateRequired';
import { vatDescription } from '../../../../helpers/vatDescription';

interface IRegistrationState {
  formFieldsPersonalData: any[];
  formFieldsBilling: any[];
  formValues: any;
  modals: any;
  errors: {
    Firstname: boolean;
    Lastname: boolean;
    Password: boolean;
    Email: boolean;
    Phone: boolean;
    AddressZip: boolean;
    AddressLine1: boolean;
    AddressCity: boolean;
    AddressCountyState: boolean;
    AddressCountry: boolean;
    DeliveryAddressLine1: boolean;
    DeliveryAddressCity: boolean;
    DeliveryAddressZip: boolean;
    DeliveryAddressCountyState: boolean;
    DeliveryAddressCountry: boolean;
  };
}

interface IRegistrationProps extends RouteComponentProps<{ language: string }> {
  register?: MutationFunc;
  countries?: any;
}

class Registration extends React.Component<IRegistrationProps> {
  
  public state: IRegistrationState;

  constructor(props: IRegistrationProps) {
    super(props);

    const { country } = CountryService.GetInstance();

    this.state = {
      formFieldsPersonalData: registrationPersonalDataFields,
      formFieldsBilling: registrationBillingFields,
      formValues: {
        AddressCountry: country.name,
        DeliveryAddressCountry: country.name,
      } as any,
      modals: {
        message: false
      },
      errors: {
        Firstname: false,
        Lastname: false,
        Password: false,
        Email: false,
        Phone: false,
        // fakturační adresa
        AddressLine1: false,
        AddressZip: false,
        AddressCity: false,
        AddressCountyState: false,
        AddressCountry: false,
        //  při jiné adrese
        DeliveryAddressLine1: false,
        DeliveryAddressCity: false,
        DeliveryAddressZip: false,
        DeliveryAddressCountyState: false,
        DeliveryAddressCountry: false,
      },
    };

    this.handleChange = this.handleChange.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  public handleChange(value: any, field: string) {
    this.setState({ formValues: { ...this.state.formValues, [field]: value } });
  }

  public setFormState(fields: any) {
    this.setState({ fields });
  }

  public async handleSend() {
    const valid = this.isValid();
    if (valid) {
      this.insert();
    }
  }

  public toggleModal(modal: string, open?: boolean) {
    if (open === undefined) {
      open = !this.state.modals[modal];
    }

    this.setState({ modals: { ...this.state.modals, [modal]: open } });
  }

  private isValid() {
    const data = this.state.formValues;
    const errors = {...this.state.errors};

    /* tslint:disable:no-string-literal */
    const valid = Object.keys(this.state.errors).reduce((prev: boolean, next: string) => {
      errors[next] = false;
      if (next === 'AddressCountyState' || (next === 'DeliveryAddressCountyState' && data['AnotherDeliveryAddress'] === true)) {
        // země pro kontrolu státtu
        const countryForCheck = (next === 'DeliveryAddressCountyState' && data['AnotherDeliveryAddress'] === true)
          ? this.getDeliveryCountry()
          : this.getInvoiceCountry();

        // stát je povinný
        if (getCountyStateRequired(countryForCheck) && !data[next]) {
          errors[next] = true;
          prev = false;
        }
      
      } else if (next.substr(0, 15) === 'DeliveryAddress') {
        if (data['AnotherDeliveryAddress'] === true) {
          if (!data[next]) {
            errors[next] = true;
            prev = false;
          }
        }
      } else if (!data[next]) {
        errors[next] = true;
        prev = false;
      }
      
      return prev;
    }, true);
    /* tslint:enable:no-string-literal */

    this.setState({
      errors,
    });

    return valid;
  }

  private getInvoiceCountry() {
    const { countries } = this.props;
    const { formValues } = this.state;
    
    return countries && !countries.loading && countries.countries 
      ? countries.countries.find((c: GQL.ICountry) => c.name === formValues.AddressCountry)
      : null;
  }

  private getDeliveryCountry() {
    const { countries } = this.props;
    const { formValues } = this.state;
    
    return countries && !countries.loading && countries.countries 
      ? countries.countries.find((c: GQL.ICountry) => c.name === formValues.DeliveryAddressCountry)
      : null;
  }

  private renderInput(name: string, label: string, options: any = {}) {
    const data = {...this.state.formValues};
    
    let element: any = null;

    if (options.type && options.type === 'select') {
      element = (
        <Select 
          name={name}
          label={label}
          placeholder={options.placeholder !== undefined ? options.placeholder : null}
          required={options.required !== undefined ? options.required === true : false}
          options={options.options || undefined}
          value={data[name] || options.defaultValue || ''}
          clearFilterDisabled={true}
          onChange={(e: any) => this.onChange({ target: { name, value: e.target.value } })}
        />
      );
    } else {
      element = (
        <Input
          type={options.type || 'text'}
          name={name}
          label={label}
          required={options.required !== undefined ? options.required === true : false}
          value={data[name] || ''}
          onChange={this.onChange}
        />
      );
    }

    const hasError = this.state.errors[name] === true;

    return (
      <div className={`form__item${hasError ? ' hasError' : ''}`}>
        {element}
        {this.state.errors[name] === true &&
          <div className="form__error">Please enter value</div>}
      </div>
    );
  }

  public render() {
    const { country, countryCode } = CountryService.GetInstance();
    const { formValues } = this.state;
    const { countries } = this.props;

    const countryOptions = countries && !countries.loading && countries.countries 
      ? countries.countries.map((c: GQL.ICountry) => {
          return {
            value: c.name,
            label: c.name,
          };
        })
      : [];

    // státy zemí, fakturační a dodací
    const countryStates = this.getCountryStatesToSelect(formValues.AddressCountry);
    const deliveryCountryStates = this.getCountryStatesToSelect(formValues.DeliveryAddressCountry);

    return (
      <React.Fragment>
        <div className="form form--registration">
          <div className="row">
            <div className="col-md-6">
              <div className="registration__block">
                <Title type="sub">Personal data</Title>
                <div>
                  {this.renderInput('Email', 'E-mail', { required: true })}
                  {this.renderInput('Phone', 'Phone', { required: true })}
                  {this.renderInput('Password', 'Password', { type: 'password', required: true })}
                  {this.renderInput('Firstname', 'First name', { required: true })}
                  {this.renderInput('Lastname', 'Last name', { required: true })}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="registration__block">
                <Title type="sub">Billing information</Title>

                <div className="row">
                  <div className="col-12">
                    {this.renderInput('AddressLine1', 'Street address', { required: true })}
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    {this.renderInput('AddressLine2', 'Address line 2')}
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-8">
                    {this.renderInput('AddressCity', 'City', { required: true })}
                  </div>
                  <div className="col-sm-4">
                    {this.renderInput('AddressZip', 'ZIP', { required: true })}
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    {countryStates.length > 0 &&
                      this.renderInput('AddressCountyState', 'County / State', { 
                        type: 'select', 
                        placeholder: 'County / State', 
                        required: getCountyStateRequired(this.getInvoiceCountry()), 
                        options: countryStates 
                      })}
                    {countryStates.length === 0 &&
                      this.renderInput('AddressCountyState', 'County / State')}
                  </div>
                </div>

                {countryOptions &&
                  this.renderInput('AddressCountry', 'State', {
                    type: 'select', 
                    options: countryOptions, 
                    defaultValue: country.name
                  })}
                
                <Input
                  type="checkbox"
                  label="Different delivery address"
                  value={formValues.AnotherDeliveryAddress}
                  onChange={(e: any) => this.onChange({ target: { name: 'AnotherDeliveryAddress', value: e.target.checked } })}
                />
                {formValues.AnotherDeliveryAddress === true &&
                  <>
                    <div className="row">
                      <div className="col-12">
                        {this.renderInput('DeliveryAddressLine1', 'Street address', { required: true })}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        {this.renderInput('DeliveryAddressLine2', 'Address line 2')}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-sm-8">
                        {this.renderInput('DeliveryAddressCity', 'City', { required: true })}
                      </div>
                      <div className="col-sm-4">
                        {this.renderInput('DeliveryAddressZip', 'ZIP', { required: true })}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        {deliveryCountryStates.length > 0 &&
                          this.renderInput('DeliveryAddressCountyState', 'County / State', { 
                            type: 'select', 
                            placeholder: 'County / State', 
                            required: getCountyStateRequired(this.getDeliveryCountry()), 
                            options: deliveryCountryStates
                          })}
                        {deliveryCountryStates.length === 0 &&
                          this.renderInput('DeliveryAddressCountyState', 'County / State')}
                      </div>
                    </div>
                    {countryOptions &&
                      this.renderInput('DeliveryAddressCountry', 'State', {
                        type: 'select',
                        options: countryOptions,
                        defaultValue: country.name
                      })}
                  </>}

                <Input
                  type="checkbox"
                  label="Company information"
                  value={formValues.CompanyInformation}
                  onChange={(e: any) => this.onChange({ target: { name: 'CompanyInformation', value: e.target.checked } })}
                />
                {formValues.CompanyInformation === true &&
                  <>
                    {this.renderInput('CompanyName', 'Company name')}
                    {this.renderInput('CompanyVatNumber', `Company ${vatDescription()} number`)}
                    {this.renderInput('CompanyRegistrationNumber', 'Company registr. number')}
                  </>}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12 d-flex form__submit">
              <CheckBox
                checked={this.state.formValues.SaveSubscribe === true}
                label="I want to subscribe to your newsletter"
                onChange={(e: any) => this.handleChange(e.target.checked, 'SaveSubscribe')}
              />
            </div>
            <div className="col-sm-12 form__submit">
              <Button
                type="xl"
                onClick={() => {
                  if (this.isValid()) {
                    this.handleSend();
                  }
                }}
              >
                Create account
              </Button>
            </div>
          </div>
        </div>
        <Modal
          type="message"
          open={this.state.modals.message}
          onClose={() => this.toggleModal('message', false)}
        >
          <div className="modal__title">Your account was created!</div>
          <Link to={`/${countryCode}/login`} className="link">
            Log in
          </Link>
        </Modal>
      </React.Fragment>
    );
  }

  private getCountryStatesToSelect(countryName: string) {
    const { countries } = this.props;
    if (
      countryName
        && countries
        && !countries.loading
        && countries.countries
    ) {
      const country = countries.countries.find((c: GQL.ICountry) => {
        return c.name === countryName;
      });
      if (country && country.states) {
        return country.states.map((s: GQL.ICountryState) => {
          return {
            value: s.name,
            label: s.name,
          };
        });
      }
    }

    return [];
  }

  private async insert() {
    if (this.props.register == undefined) {
      return;
    }

    const values = this.state.formValues;

    const variables = {
      user: {
        email: values.Email && values.Email.trim().toLowerCase(),
        password: values.Password,
        phone: values.Phone,
        // fakturační údaje
        invoiceFirstName: values.Firstname,
        invoiceLastName: values.Lastname,
        invoiceStreet: values.AddressStreet,
        invoiceHouseNumber: values.AddressStreetNumber,
        invoiceAddressLine1: values.AddressLine1,
        invoiceAddressLine2: values.AddressLine2 || null,
        invoiceZip: values.AddressZip,
        invoiceCity: values.AddressCity,
        invoiceCountyState: values.AddressCountyState || null,
        invoiceCountry: values.AddressCountry,
        // jiná doručovací adresa
        anotherDeliveryAddress: values.AnotherDeliveryAddress,
        deliveryStreet: values.AnotherDeliveryAddress === true ? values.DeliveryAddressStreet : null,
        deliveryHouseNumber: values.AnotherDeliveryAddress === true ? values.DeliveryAddressStreetNumber : null,
        deliveryAddressLine1: values.AnotherDeliveryAddress === true ? values.DeliveryAddressLine1 : null,
        deliveryAddressLine2: values.AnotherDeliveryAddress === true ? values.DeliveryAddressLine2 || null : null,
        deliveryZip: values.AnotherDeliveryAddress === true ? values.DeliveryAddressZip : null,
        deliveryCity: values.AnotherDeliveryAddress === true ? values.DeliveryAddressCity : null,
        deliveryCountyState: values.AnotherDeliveryAddress === true ? values.DeliveryAddressCountyState || null : null,
        deliveryCountry: values.AnotherDeliveryAddress === true ? values.DeliveryAddressCountry : null,
        // firma
        companyInformation: values.CompanyInformation,
        companyName: values.CompanyInformation === true ? values.CompanyName : null,
        companyVatNumber: values.CompanyInformation === true ? values.CompanyVatNumber : null,
        companyRegistrationNumber: values.CompanyInformation === true ? values.CompanyRegistrationNumber : null,
        // přihlášení k odběru novinek
        receiveNewsletter: values.SaveSubscribe,
      }
    };

    let register: any;
    try {
      register = await this.props.register({ variables });
    } catch (err) {
      // ošetření chyb
      if (err) {
        // email je již zaregistrován
        const emailIsTaken = err.message.search('Email is taken');
        if (emailIsTaken) {
          FlashStore.GetInstance().showMessage(`This email is already in use`, 'error');
        }
      }
    }

    if (register) {
      this.setState({ modals: { ...this.state.modals, ['message']: true } });
      this.setState({ formValues: {} });
    }
  }

  private onChange(e: any) {
    const { name, value, checked } = e.target;

    let newValue = value !== undefined
      ? value
      : checked;

    if (newValue instanceof Object && newValue.value) {
      newValue = newValue.value;
    }

    this.setState({
      formValues: { ...this.state.formValues, [name]: newValue }
    });
  }
}

export default withRouter(Registration);



// WEBPACK FOOTER //
// ./src/components/Query/User/Registration/Registration.tsx